import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpErrorHandler } from '../utils/http-error-handler';

export interface TenantEmailConfiguration {
  key?: string;
  tenantId: string;
  graphTenantId: string;
  graphClientId: string;
  graphClientSecretKeyVaultName?: string;
  isClientSecretConfigured: boolean;
  systemSenderEmail: string;
  isActive: boolean;
  invitationEmailSubject?: string;
  invitationEmailBody?: string;
  isNotificationEmailEnabled: boolean;
  notificationEmailSubject?: string;
  notificationEmailBody?: string;
  isLocalDevelopment?: boolean;
  createdAtUtc?: string;
  modifiedAtUtc?: string;
}

export interface SaveEmailConfigurationRequest {
  graphTenantId: string;
  graphClientId: string;
  graphClientSecret?: string | null;
  systemSenderEmail: string;
  isActive: boolean;
  invitationEmailSubject?: string;
  invitationEmailBody?: string;
  isNotificationEmailEnabled: boolean;
  notificationEmailSubject?: string;
  notificationEmailBody?: string;
}

export interface SaveEmailConfigurationResponse {
  message: string;
  tenantId: string;
}

export interface TestEmailResponse {
  success: boolean;
  message: string;
  notificationDisabledWarning?: boolean;
}

@Injectable({ providedIn: 'root' })
export class TenantEmailConfigurationService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = '/api/email/configuration';
  private readonly testInvitationApiUrl = '/api/email/test-send/invitation';
  private readonly testNotificationApiUrl = '/api/email/test-send/notification';

  getConfiguration(): Observable<TenantEmailConfiguration> {
    return this.http.get<TenantEmailConfiguration>(this.apiUrl).pipe(
      catchError((error: HttpErrorResponse) => 
        HttpErrorHandler.handleError(error, 'Failed to load email configuration')
      )
    );
  }

  saveConfiguration(request: SaveEmailConfigurationRequest): Observable<SaveEmailConfigurationResponse> {
    return this.http.post<SaveEmailConfigurationResponse>(this.apiUrl, request).pipe(
      catchError((error: HttpErrorResponse) => 
        HttpErrorHandler.handleError(error, 'Failed to save email configuration')
      )
    );
  }

  sendTestInvitationEmail(request: { subject: string; body: string }): Observable<TestEmailResponse> {
    return this.http.post<TestEmailResponse>(this.testInvitationApiUrl, request).pipe(
      catchError((error: HttpErrorResponse) =>
        HttpErrorHandler.handleError(error, 'Failed to send test invitation email')
      )
    );
  }

  sendTestNotificationEmail(request: { subject: string; body: string; isNotificationEnabled: boolean }): Observable<TestEmailResponse> {
    return this.http.post<TestEmailResponse>(this.testNotificationApiUrl, request).pipe(
      catchError((error: HttpErrorResponse) =>
        HttpErrorHandler.handleError(error, 'Failed to send test notification email')
      )
    );
  }
}
